import { call } from 'redux-saga/effects';

/**
 * Любая неизвестная ошибка
 */
export class UnexpectedError extends Error {
    data?: any;

    constructor(message: string, data?: any, cause?: Error) {
        super(message);
        this.name = 'UnexpectedError';
        this.cause = cause;
        this.data = data;
    }

    toJSON() {
        return {
            data: this?.data,
            cause: this?.cause,
        };
    }
}
export class UnexpectedSagaError extends Error {
    constructor(message: string, public cause: Error, public sagaName: string, public module?: string) {
        super(message);
        this.name = 'UnexpectedSagaError';
    }
}

/**
 * Превращает обработчик saga события в безопасный.
 * В случае возникновения ошибки внутри saga, ошибка будет обработана и залогирована.
 * Необходимо использовать при создании подписок на события.
 *
 * Оборачивание обеспечит стабильную обработку событий.
 *
 * @param saga Функция обработчик события.
 * @param module Модуль, в котором находится обработчик события.
 * @returns Возвращает такую же функцию, но без возможности выбросить исключение выше.
 *
 * @category Duck Handlers
 *
 * @example
 * // Для модулей второй версии
 * function* subscriptions() {
 *      yield all([
 *          takeEvery(apiHandler.FETCH_DONE, safeSaga(handleApiHandlerDoneEvent, PREFIX)),
 *      ]);
 * }
 *
 * @example
 * // Для модулей первой версии
 * function* rootSaga() {
 *      yield all([
 *          ...apiHandler.effects,
 *          takeEvery(apiHandler.FETCH_DONE, safeSaga(handleApiHandlerDoneEvent, PREFIX)),
 *      ]);
 * }
 */
export function safeSaga<T>(saga: T, module?: string): T {
    return function* (...args) {
        try {
            return yield call(saga as any, ...args);
        } catch (cause) {
            const error = new UnexpectedSagaError(
                cause?.message ?? 'Unexpected error on saga call',
                cause,
                (saga as any).name,
                module
            );

            console.error(error);
        }
    } as T;
}